import { DatePicker as $DatePicker } from 'antd';
import { DatePickerProps as $DatePickerProps } from 'antd/lib/date-picker/interface';
import React, { useEffect, useState } from 'react';
import moment from 'moment'; // this is a webpack alias for `dayjs`
import { Dayjs } from 'dayjs';

import { OnChangeCallback, OnBlurCallback } from '.';

export type DatePickerProps = Omit<$DatePickerProps, 'onChange' | 'value'> & {
  value?: moment.Moment | Dayjs | string | null;
  resetTime?: boolean;
  onChange?: OnChangeCallback;
  onBlur?: OnBlurCallback;
};

const DEFAULT_TIME_FORMAT = 'hh:mm A';

const createCustomDate = (date: Dayjs) => {
  if (!date) {
    return;
  }

  const newDate = date.add(2, 'hour');

  return newDate.set('minute', 0).set('second', 0);
};

const DatePicker = React.memo(function DatePicker({
  name,
  value: origValue,
  placeholder = 'mm/dd/yyyy',
  format = 'MM/DD/YYYY',
  showTime = false,
  showToday = false,
  resetTime = true,
  onChange,
  onBlur,
  onOpenChange,
  ...rest
}: DatePickerProps) {
  const initialValue = typeof origValue === 'string' ? moment(origValue) : origValue;
  const [value, setValue] = useState(initialValue);
  const handleChange = (date: any) => {
    if (date && !showTime && resetTime) {
      // set to 00:00:00.000
      date.hour(0);
      date.minute(0);
      date.second(0);
      date.millisecond(0);
    }
    // `null` will cause `InvalidDate` error on yup validation, set to `undefined` explicitly
    const value = date || undefined;

    setValue((prevState) => (prevState ? value : createCustomDate(value)));

    if (onChange) {
      onChange({ name, value });
    }
  };

  const handleOpenChange = (status: boolean) => {
    // simulate `onBlur` when the picker is closed
    if (onBlur && !status) {
      onBlur({ name, value });
    }
    if (onOpenChange) {
      onOpenChange(status);
    }
  };

  function disabledDate(current: any) {
    if (name === 'dob') {
      return current && current > moment().endOf('day');
    }
  }

  useEffect(() => {
    if (!initialValue || !value || !initialValue.isSame(value)) {
      setValue(initialValue);
    }
  }, [initialValue, value]);

  return (
    <$DatePicker
      format={showTime ? `${format} ${DEFAULT_TIME_FORMAT}` : format}
      placeholder={placeholder}
      disabledDate={disabledDate}
      showTime={
        showTime
          ? {
              use12Hours: true,
              format: DEFAULT_TIME_FORMAT,
              defaultValue: moment('00:00', 'HH:mm'),
            }
          : false
      }
      showToday={showToday}
      {...rest}
      name={name}
      value={value}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      getCalendarContainer={(triggerNode) => triggerNode.parentElement as HTMLElement}
    />
  );
});

export default DatePicker;
